// Parsers for the <guide-output> bullets returned by the intake guide
// chat. The AI system prompt (see app/services/intake_guide_service.rb)
// instructs the model to output a strict bullet format per section;
// these helpers tolerate common formatting drift (em-dash vs colon,
// extra whitespace) without silently dropping good bullets.

// Parses KPI / KOI bullets that follow "Name: Target" / "Name — Target" /
// "Name - Target". Returns [{name, target}] with non-parseable bullets
// dropped. Empty arrays are legal — the caller decides how to handle.
//
//   parseNamedTargetBullets([
//     "Weekly discovery calls: 10",
//     "Monthly recurring revenue — $50k",
//   ])
//   // => [{name: "Weekly discovery calls", target: "10"},
//   //     {name: "Monthly recurring revenue", target: "$50k"}]
export function parseNamedTargetBullets(bullets) {
  if (!Array.isArray(bullets)) return []
  return bullets
    .map((raw) => splitNameTarget(String(raw || "").trim()))
    .filter((row) => row && row.name && row.target)
}

// Parses one-year-plan bullets, which are a mixed structure:
//   - Revenue: $750,000
//   - Profit: $150,000
//   - Measurables: NPS above 50, retention above 80%
//   - Issue: Hire an ops lead
//   - Issue: Fix billing
//
// Returns { revenueTarget, profitTarget, measurables, issues } where
// numeric fields are bare number strings (stripped of $ and commas,
// ready for CurrencyUsdInput / the one-year-plan form) and issues is an array
// of plain-text strings. Missing labels are returned as null / "" /
// empty array so the caller can merge defensively without overwriting
// anything the user already typed.
export function parseOneYearPlanBullets(bullets) {
  const out = {
    targetDate: null,
    revenueTarget: null,
    profitTarget: null,
    measurables: null,
    issues: [],
  }
  if (!Array.isArray(bullets)) return out

  for (const raw of bullets) {
    const line = String(raw || "")
      .replace(/^[-*•]\s*/, "")
      .trim()
    if (!line) continue

    const match = line.match(/^([A-Za-z]+)\s*:\s*(.+)$/)
    if (!match) continue
    const label = match[1].toLowerCase()
    const value = match[2].trim()
    if (!value) continue

    switch (label) {
      case "targetdate":
      case "target_date":
      case "date":
        out.targetDate = normalizeIsoDate(value)
        break
      case "revenue":
      case "revenuetarget":
        out.revenueTarget = stripMoney(value)
        break
      case "profit":
      case "profittarget":
        out.profitTarget = stripMoney(value)
        break
      case "measurables":
      case "measurable":
        out.measurables = value
        break
      case "issue":
      case "issues":
        out.issues.push(value)
        break
      default:
        // Ignore unknown labels — the prompt restricts the label set,
        // but a noisy model isn't worth failing the whole insert over.
        break
    }
  }
  return out
}

// Pull an ISO date out of whatever the AI hands us. Accepts
// "2027-04-22", "April 22, 2027", "Apr 22 2027", etc. Falls back to
// null if we can't confidently produce a YYYY-MM-DD string — better
// to leave the field alone than to stamp an invalid date on it.
function normalizeIsoDate(raw) {
  const s = String(raw).trim()
  const iso = s.match(/\b(\d{4}-\d{2}-\d{2})\b/)
  if (iso) return iso[1]
  const parsed = Date.parse(s)
  if (Number.isNaN(parsed)) return null
  const d = new Date(parsed)
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, "0")
  const day = String(d.getUTCDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

// Strip $ / commas / trailing "k" or "m" abbreviations and return a
// bare number string. "k" and "m" are common in AI output so we expand
// them rather than forcing the user to.
function stripMoney(raw) {
  let s = String(raw).trim().replace(/[$,]/g, "")
  const suffix = s.match(/^([0-9]*\.?[0-9]+)\s*([kKmM])$/)
  if (suffix) {
    const num = parseFloat(suffix[1])
    const mult = suffix[2].toLowerCase() === "m" ? 1_000_000 : 1_000
    return String(Math.round(num * mult))
  }
  // Keep only digits and a single decimal point — the <input type="number">
  // is already strict, but the consumer usually wants a clean string.
  const numMatch = s.match(/-?\d+(\.\d+)?/)
  return numMatch ? numMatch[0] : s
}

// Splits a single bullet into {name, target}. Preference order for the
// separator matches how models most often format their output:
//   1. " — " (em-dash w/ spaces)
//   2. " – " (en-dash w/ spaces)
//   3. ":" (first colon only, so targets with colons like "5:00am" survive)
//   4. " - " (hyphen w/ spaces — last because it's noisy inside KPI names)
function splitNameTarget(line) {
  if (!line) return null
  const cleaned = line.replace(/^[-*•]\s*/, "").trim()
  if (!cleaned) return null

  const separators = [" — ", " – ", ":", " - "]
  for (const sep of separators) {
    const idx = cleaned.indexOf(sep)
    if (idx > 0) {
      const name = cleaned.slice(0, idx).trim()
      const target = cleaned.slice(idx + sep.length).trim()
      if (name && target) return { name, target }
    }
  }
  // No separator found — the bullet doesn't carry both pieces. Return
  // the whole thing as the name with an empty target so the user can
  // fill in the target manually rather than losing the bullet entirely.
  return { name: cleaned, target: "" }
}
